import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Divider,
    Grid,
    Autocomplete,
    Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useDishStore from '../../store/dishStore';
import useIngredientStore from '../../store/ingredientStore';
import useRestaurantStore from '../../store/restaurantStore';
import useTagStore from '../../store/tagStore';

const DishEditDialog = ({ open, dish, onClose, onSave, mode='edit' }) => {
    const { t } = useTranslation();
    const isEditing = !!dish;
    const { deleteDish } = useDishStore();
    const { ingredients, loading: ingredientsLoading, fetchIngredients } = useIngredientStore();
    const { restaurants, loading: restaurantsLoading, fetchRestaurants } = useRestaurantStore();
    const { tags, fetchTags } = useTagStore();

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        style: '',
        restaurant_id: null,
        ingredients: [],
        tags: [] // Array of tag names
    });

    // State for current ingredient being added
    const [currentIngredient, setCurrentIngredient] = useState({
        name: '',
        amount: '',
        measure_unit: ''
    });

    useEffect(() => {
        if (dish) {
            // Parse tags from comma-separated string to array
            const dishTags = dish.tags ? dish.tags.split(',').map(t => t.trim()).filter(t => t) : [];
            setFormData({
                name: dish.name || '',
                description: dish.description || '',
                style: dish.style || '',
                restaurant_id: dish.restaurant_id || null,
                ingredients: dish.recipe || [],
                tags: dishTags
            });
        } else {
            setFormData({
                name: '',
                description: '',
                style: '',
                restaurant_id: null,
                ingredients: [],
                tags: []
            });
        }
        
        // Fetch data for autocompletes
        fetchIngredients();
        fetchRestaurants();
        fetchTags();
    }, [dish, fetchIngredients, fetchRestaurants, fetchTags]);

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    // Handle restaurant selection
    const handleRestaurantChange = (event, value) => {
        setFormData(prev => ({
            ...prev,
            restaurant_id: value ? value.id : null
        }));
    };

    // Ingredient form handlers
    const handleIngredientChange = (field) => (event) => {
        setCurrentIngredient(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };
    
    // Handle autocomplete selection
    const handleIngredientAutocomplete = (event, value) => {
        if (value) {
            // If an existing ingredient is selected
            setCurrentIngredient(prev => ({
                ...prev,
                name: value.name,
                // Optionally pre-fill default units if they exist in your ingredient data
                measure_unit: value.measure_unit || prev.measure_unit
            }));
        }
    };

    const addIngredient = () => {
        if (!currentIngredient.name) return;
        
        setFormData(prev => ({
            ...prev,
            ingredients: [...prev.ingredients, {...currentIngredient}]
        }));
        
        // Reset form
        setCurrentIngredient({
            name: '',
            amount: '',
            measure_unit: ''
        });
    };

    const removeIngredient = (index) => {
        setFormData(prev => ({
            ...prev,
            ingredients: prev.ingredients.filter((_, i) => i !== index)
        }));
    };

    // Tag handlers
    const toggleTag = (tagName) => {
        setFormData(prev => {
            const isAssigned = prev.tags.includes(tagName);
            if (isAssigned) {
                // Remove tag
                return {
                    ...prev,
                    tags: prev.tags.filter(t => t !== tagName)
                };
            } else {
                // Add tag
                return {
                    ...prev,
                    tags: [...prev.tags, tagName]
                };
            }
        });
    };

    const handleSave = () => {
        // Convert tags array to comma-separated string for backend
        const dataToSave = {
            ...formData,
            tags: formData.tags.join(', ')
        };
        onSave(dataToSave);
        onClose();
    };

    const handleDelete = async (e) => {
        try {
            console.log(`deleting ${dish.id}`);
            await deleteDish(dish.id);
            onClose();
        } catch (error) {
            console.error(t('dishes.failedToDelete'), error);
        }
    };

    // Handle Enter key to add ingredient
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addIngredient();
        }
    };

    // Get selected restaurant for display
    const selectedRestaurant = restaurants.find(r => r.id === formData.restaurant_id);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {mode === 'edit' ? t('dishes.editDish', { name: dish?.name }) : t('dishes.addNewDish')}        
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('dishes.dishName')}
                        value={formData.name}
                        onChange={handleInputChange('name')}
                        fullWidth
                    />

                    <TextField
                        label={t('common.labels.description')}
                        value={formData.description}
                        onChange={handleInputChange('description')}
                        fullWidth
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={t('dishes.style')}
                                value={formData.style}
                                onChange={handleInputChange('style')}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={restaurants}
                                loading={restaurantsLoading}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedRestaurant || null}
                                onChange={handleRestaurantChange}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label={t('dishes.restaurant')} 
                                        fullWidth
                                        helperText={t('dishes.restaurantHelperText')}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body1">{option.name}</Typography>
                                            {option.cuisine_type && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {option.cuisine_type}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* Tags Section */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('dishes.addOrRemoveTags', 'Add or remove tags')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('dishes.tagInstructions', 'Add or remove via drag&drop')}
                        </Typography>

                        {/* Assigned Tags */}
                        <Box sx={{ 
                            backgroundColor: '#FFFACD', 
                            p: 2, 
                            borderRadius: 1, 
                            mb: 2,
                            minHeight: 60
                        }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('dishes.assignedTags', 'Assigned tags')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {formData.tags.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('dishes.noTagsAssigned', 'No tags assigned. Click tags below to add them.')}
                                    </Typography>
                                ) : (
                                    formData.tags.map((tagName) => {
                                        const tag = tags.find(t => t.name === tagName);
                                        if (!tag) return null;
                                        return (
                                            <Chip
                                                key={tag.id}
                                                label={tag.name}
                                                icon={<span>{tag.icon}</span>}
                                                onClick={() => toggleTag(tag.name)}
                                                sx={{
                                                    backgroundColor: tag.color,
                                                    color: '#000',
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        opacity: 0.8,
                                                    }
                                                }}
                                            />
                                        );
                                    })
                                )}
                            </Box>
                        </Box>

                        {/* All Tags */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('dishes.allTags', 'All tags')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {tags.map((tag) => {
                                    const isAssigned = formData.tags.includes(tag.name);
                                    if (isAssigned) return null; // Don't show assigned tags here
                                    return (
                                        <Chip
                                            key={tag.id}
                                            label={tag.name}
                                            icon={<span>{tag.icon}</span>}
                                            onClick={() => toggleTag(tag.name)}
                                            sx={{
                                                backgroundColor: tag.color,
                                                color: '#000',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    opacity: 0.8,
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>

                    {/* Ingredients Section */}
                    <Typography variant="h6" sx={{ mt: 2 }}>{t('common.labels.ingredients')}</Typography>
                    
                    {/* Ingredient List */}
                    {formData.ingredients.length > 0 && (
                        <List sx={{ bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                            {formData.ingredients.map((ingredient, index) => (
                                <React.Fragment key={index}>
                                    <ListItem 
                                        secondaryAction={
                                            <IconButton edge="end" onClick={() => removeIngredient(index)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText 
                                            primary={ingredient.name} 
                                            secondary={
                                                ingredient.amount && ingredient.measure_unit ? 
                                                `${ingredient.amount} ${ingredient.measure_unit}` : t('dishes.noAmount')
                                            } 
                                        />
                                    </ListItem>
                                    {index < formData.ingredients.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}

                    {/* Ingredient Form */}
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                            <Autocomplete
                                options={ingredients}
                                loading={ingredientsLoading}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={handleIngredientAutocomplete}
                                onInputChange={(event, newInputValue) => {
                                    setCurrentIngredient(prev => ({
                                        ...prev,
                                        name: newInputValue
                                    }));
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label={t('dishes.ingredientName')} 
                                        value={currentIngredient.name}
                                        fullWidth
                                        onKeyPress={handleKeyPress}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextField
                                label={t('dishes.amount')}
                                type="number"
                                value={currentIngredient.amount}
                                onChange={handleIngredientChange('amount')}
                                fullWidth
                                onKeyPress={handleKeyPress}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                label={t('dishes.unit')}
                                value={currentIngredient.measure_unit}
                                onChange={handleIngredientChange('measure_unit')}
                                fullWidth
                                onKeyPress={handleKeyPress}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Button 
                                variant="contained" 
                                color="primary"
                                onClick={addIngredient}
                                fullWidth
                                startIcon={<AddIcon />}
                            >
                                {t('common.buttons.add')}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t('common.buttons.cancel')}</Button>
                <Button onClick={handleSave} variant="contained" color="primary">{t('common.buttons.save')}</Button>
                {mode === 'edit' && (
                    <Button onClick={handleDelete} color="error">{t('common.buttons.delete')}</Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DishEditDialog;